import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import AnnouncementService from '../services/AnnouncementService';
import { Button, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ResponsiveTable from './ResponsiveTable';
import { format } from 'date-fns';
import { BsPencil, BsTrash } from 'react-icons/bs';

const Announcements = () => {
    const { user } = useContext(AuthContext);
    const [announcements, setAnnouncements] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        text: '',
        expiresAt: ''
    });

    useEffect(() => {
        loadAnnouncements();
    }, []);

    const loadAnnouncements = async () => {
        try {
            const data = await AnnouncementService.getActiveAnnouncements();
            setAnnouncements(data);
        } catch (error) {
            console.error('Error loading announcements:', error);
            toast.error('Failed to load announcements');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (selectedAnnouncement) {
                await AnnouncementService.updateAnnouncement(selectedAnnouncement.id, {
                    ...formData,
                    expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : null
                });
                toast.success('Announcement updated successfully');
            } else {
                await AnnouncementService.createAnnouncement({
                    ...formData,
                    expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : null,
                    createdById: user.userId
                });
                toast.success('Announcement created successfully');
            }
            setShowModal(false);
            setFormData({ title: '', text: '', expiresAt: '' });
            setSelectedAnnouncement(null);
            loadAnnouncements();
        } catch (error) {
            console.error('Error saving announcement:', error);
            toast.error('Failed to save announcement');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this announcement?')) {
            try {
                await AnnouncementService.deleteAnnouncement(id);
                toast.success('Announcement deleted successfully');
                loadAnnouncements();
            } catch (error) {
                console.error('Error deleting announcement:', error);
                toast.error('Failed to delete announcement');
            }
        }
    };

    const handleEdit = (announcement) => {
        setSelectedAnnouncement(announcement);
        setFormData({
            title: announcement.title,
            text: announcement.text,
            expiresAt: announcement.expiresAt ? format(new Date(announcement.expiresAt), "yyyy-MM-dd'T'HH:mm") : ''
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAnnouncement(null);
        setFormData({ title: '', text: '', expiresAt: '' });
    };

    const columns = [
        { key: 'title', label: 'Title' },
        { key: 'text', label: 'Text' },
        { 
            key: 'createdAt', 
            label: 'Created At',
            render: (value) => format(new Date(value), 'PPPpp')
        },
        { 
            key: 'expiresAt', 
            label: 'Expires At',
            render: (value) => value ? format(new Date(value), 'PPPpp') : 'Never'
        },
        { key: 'createdByUsername', label: 'Created By' }
    ];

    const actions = [
        {
            icon: <BsPencil />,
            variant: "outline-primary",
            onClick: handleEdit
        },
        {
            icon: <BsTrash />,
            variant: "outline-danger",
            onClick: (item) => handleDelete(item.id)
        }
    ];

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Announcements</h2>
                <Button variant="primary" onClick={() => setShowModal(true)}>
                    Create Announcement
                </Button>
            </div>

            <ResponsiveTable 
                data={announcements}
                columns={columns}
                actions={actions}
            />

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Text</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.text}
                                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Expiration Date (Optional)</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={formData.expiresAt}
                                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                            />
                        </Form.Group>
                        <Button variant="primary" type="submit">
                            {selectedAnnouncement ? 'Update' : 'Create'}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Announcements; 